import type { PrintFormatV1 } from './types'

export interface PrintFormatReceiptBlocks {
  header: boolean
  orderMeta: boolean
  lineItems: boolean
  totals: boolean
  payment: boolean
  footer: boolean
}

export interface PrintFormatReceiptV2 {
  version: 2
  blocks: PrintFormatReceiptBlocks
  header: {
    showLogo: boolean
    showCompanyDetails: boolean
    customLines: string[]
  }
  lineItems: {
    /** When true, print `@ {unit}` under each item (line total still on the name row). */
    showUnitPrice: boolean
  }
  totals: {
    showSubtotal: boolean
    showCharges: boolean
    showGrandTotal: boolean
  }
  payment: {
    showMethodAndAmount: boolean
    showChange: boolean
  }
  orderMeta: {
    showCustomerName: boolean
    showTable: boolean
    showStaff: boolean
  }
  footer: {
    lines: string[]
    showQr: boolean
    qrUrl: string
    showPrintedTime: boolean
    showWatermark: boolean
  }
  copies: {
    /** When true, print a merchant copy after the customer receipt by default. */
    includeMerchantCopy: boolean
    /** When true, print CUSTOMER COPY / MERCHANT COPY at the top of each slip. */
    showCopyLabel: boolean
  }
  advancedTemplate?: string | null
}

export type PrintFormatCustomerReceipt = PrintFormatV1 | PrintFormatReceiptV2

export function isCustomerReceiptV2(
  cfg: PrintFormatCustomerReceipt,
): cfg is PrintFormatReceiptV2 {
  return cfg != null && typeof cfg === 'object' && (cfg as PrintFormatReceiptV2).version === 2
}

export function defaultPrintFormatReceiptV2(): PrintFormatReceiptV2 {
  return {
    version: 2,
    blocks: {
      header: true,
      orderMeta: true,
      lineItems: true,
      totals: true,
      payment: true,
      footer: true,
    },
    header: {
      showLogo: false,
      showCompanyDetails: false,
      customLines: [],
    },
    lineItems: {
      showUnitPrice: false,
    },
    totals: {
      showSubtotal: true,
      showCharges: true,
      showGrandTotal: true,
    },
    payment: {
      showMethodAndAmount: true,
      showChange: true,
    },
    orderMeta: {
      showCustomerName: true,
      showTable: true,
      showStaff: true,
    },
    footer: {
      lines: [],
      showQr: false,
      qrUrl: '',
      showPrintedTime: true,
      showWatermark: false,
    },
    copies: {
      includeMerchantCopy: false,
      showCopyLabel: true,
    },
    advancedTemplate: null,
  }
}

export function migrateCustomerReceiptV1ToV2(v1: PrintFormatV1): PrintFormatReceiptV2 {
  const v2 = defaultPrintFormatReceiptV2()
  v2.blocks.header = v1.blocks.header
  v2.blocks.orderMeta = v1.blocks.orderMeta
  v2.blocks.lineItems = v1.blocks.lineItems
  v2.blocks.totals = v1.blocks.totals
  v2.blocks.footer = v1.blocks.footer
  v2.header.customLines = [...v1.headerLines]
  v2.footer.lines = [...v1.footerLines]
  v2.advancedTemplate = v1.advancedTemplate ?? null
  return v2
}

function mergeReceiptBlocks(
  def: PrintFormatReceiptBlocks,
  raw: unknown,
): PrintFormatReceiptBlocks {
  if (!raw || typeof raw !== 'object') return def
  const b = raw as Partial<PrintFormatReceiptBlocks>
  return {
    header: typeof b.header === 'boolean' ? b.header : def.header,
    orderMeta: typeof b.orderMeta === 'boolean' ? b.orderMeta : def.orderMeta,
    lineItems: typeof b.lineItems === 'boolean' ? b.lineItems : def.lineItems,
    totals: typeof b.totals === 'boolean' ? b.totals : def.totals,
    payment: typeof b.payment === 'boolean' ? b.payment : def.payment,
    footer: typeof b.footer === 'boolean' ? b.footer : def.footer,
  }
}

export function mergePrintFormatReceiptV2(
  def: PrintFormatReceiptV2,
  raw: unknown,
): PrintFormatReceiptV2 {
  if (!raw || typeof raw !== 'object') return def
  const r = raw as Partial<PrintFormatReceiptV2>
  if (r.version !== 2) return def
  const h = (r.header && typeof r.header === 'object' ? r.header : {}) as Partial<
    PrintFormatReceiptV2['header']
  >
  const li = (r.lineItems && typeof r.lineItems === 'object' ? r.lineItems : {}) as Partial<
    PrintFormatReceiptV2['lineItems']
  >
  const t = (r.totals && typeof r.totals === 'object' ? r.totals : {}) as Partial<
    PrintFormatReceiptV2['totals']
  >
  const p = (r.payment && typeof r.payment === 'object' ? r.payment : {}) as Partial<
    PrintFormatReceiptV2['payment']
  >
  const f = (r.footer && typeof r.footer === 'object' ? r.footer : {}) as Partial<
    PrintFormatReceiptV2['footer']
  >
  const om = (r.orderMeta && typeof r.orderMeta === 'object' ? r.orderMeta : {}) as Partial<
    PrintFormatReceiptV2['orderMeta']
  >
  const c = (r.copies && typeof r.copies === 'object' ? r.copies : {}) as Partial<
    PrintFormatReceiptV2['copies']
  >
  return {
    version: 2,
    blocks: mergeReceiptBlocks(def.blocks, r.blocks),
    header: {
      showLogo: typeof h.showLogo === 'boolean' ? h.showLogo : def.header.showLogo,
      showCompanyDetails:
        typeof h.showCompanyDetails === 'boolean'
          ? h.showCompanyDetails
          : def.header.showCompanyDetails,
      customLines: Array.isArray(h.customLines)
        ? h.customLines.filter((x): x is string => typeof x === 'string')
        : def.header.customLines,
    },
    lineItems: {
      showUnitPrice:
        typeof li.showUnitPrice === 'boolean' ? li.showUnitPrice : def.lineItems.showUnitPrice,
    },
    totals: {
      showSubtotal:
        typeof t.showSubtotal === 'boolean' ? t.showSubtotal : def.totals.showSubtotal,
      showCharges: typeof t.showCharges === 'boolean' ? t.showCharges : def.totals.showCharges,
      showGrandTotal:
        typeof t.showGrandTotal === 'boolean' ? t.showGrandTotal : def.totals.showGrandTotal,
    },
    payment: {
      showMethodAndAmount:
        typeof p.showMethodAndAmount === 'boolean'
          ? p.showMethodAndAmount
          : def.payment.showMethodAndAmount,
      showChange: typeof p.showChange === 'boolean' ? p.showChange : def.payment.showChange,
    },
    orderMeta: {
      showCustomerName:
        typeof om.showCustomerName === 'boolean'
          ? om.showCustomerName
          : def.orderMeta.showCustomerName,
      showTable: typeof om.showTable === 'boolean' ? om.showTable : def.orderMeta.showTable,
      showStaff: typeof om.showStaff === 'boolean' ? om.showStaff : def.orderMeta.showStaff,
    },
    footer: {
      lines: Array.isArray(f.lines)
        ? f.lines.filter((x): x is string => typeof x === 'string')
        : def.footer.lines,
      showQr: typeof f.showQr === 'boolean' ? f.showQr : def.footer.showQr,
      qrUrl: typeof f.qrUrl === 'string' ? f.qrUrl : def.footer.qrUrl,
      showPrintedTime:
        typeof f.showPrintedTime === 'boolean' ? f.showPrintedTime : def.footer.showPrintedTime,
      showWatermark:
        typeof f.showWatermark === 'boolean' ? f.showWatermark : def.footer.showWatermark,
    },
    copies: {
      includeMerchantCopy:
        typeof c.includeMerchantCopy === 'boolean'
          ? c.includeMerchantCopy
          : def.copies.includeMerchantCopy,
      showCopyLabel:
        typeof c.showCopyLabel === 'boolean' ? c.showCopyLabel : def.copies.showCopyLabel,
    },
    advancedTemplate:
      r.advancedTemplate === undefined ? def.advancedTemplate : r.advancedTemplate,
  }
}

export function normalizeCustomerReceipt(cfg: PrintFormatCustomerReceipt): PrintFormatReceiptV2 {
  if (isCustomerReceiptV2(cfg)) return mergePrintFormatReceiptV2(defaultPrintFormatReceiptV2(), cfg)
  return migrateCustomerReceiptV1ToV2(cfg)
}
